/**
 * Crew · task detail — the booked vehicle + "report a problem" with photos.
 *
 * A crew member on a job can flag a problem with the truck they're using from
 * their phone; it lands on the vehicle's Fleet page for the supervisor to
 * resolve. Photos ride the shared attachments pipeline.
 */
import { useRef, useState } from 'react';
import { AlertTriangle, ImagePlus, Loader2, Truck } from 'lucide-react';
import { toast } from 'sonner';
import type { VehicleIssueSeverity } from '@vari-crew/schemas';
import { uploadIssuePhoto, useCreateVehicleIssue } from '@/api/vehicles';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Card, CardContent } from '@/components/ui/Card';
import { cn } from '@/lib/cn';

const SEVERITIES: { value: VehicleIssueSeverity; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
];

export function VehicleIssueCard({
  vehicleId,
  vehicleLabel,
  taskId,
}: {
  vehicleId: string | null;
  vehicleLabel: string | null;
  taskId: string;
}) {
  const [reporting, setReporting] = useState(false);
  // Nothing to report against until a vehicle is booked on the task.
  if (!vehicleId) return null;

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Truck className="w-4 h-4 text-ink-dim shrink-0" />
            <span className="text-sm text-ink-dim">Vehicle</span>
            <span className="font-mono font-medium truncate">{vehicleLabel ?? '—'}</span>
          </div>
          {!reporting && (
            <Button variant="outline" size="sm" onClick={() => setReporting(true)}>
              <AlertTriangle className="w-3.5 h-3.5" />
              Report a problem
            </Button>
          )}
        </div>
        {reporting && (
          <ReportForm
            vehicleId={vehicleId}
            taskId={taskId}
            onDone={() => setReporting(false)}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ReportForm({
  vehicleId,
  taskId,
  onDone,
}: {
  vehicleId: string;
  taskId: string;
  onDone: () => void;
}) {
  const create = useCreateVehicleIssue(vehicleId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<VehicleIssueSeverity>('normal');
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function submit() {
    if (!title.trim()) {
      toast.error('Briefly describe the problem.');
      return;
    }
    try {
      setUploading(true);
      const issue = await create.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        severity,
        task_id: taskId,
      });
      for (const f of files) {
        try {
          await uploadIssuePhoto(issue.id, f);
        } catch {
          /* best-effort photo */
        }
      }
      toast.success('Reported — your supervisor will see it on the fleet page.');
      onDone();
    } catch (e) {
      toast.error(`Failed to report: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  const busy = create.isPending || uploading;

  return (
    <div className="mt-3 space-y-2 border-t border-line pt-3">
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What's wrong? (e.g. check-engine light on)"
        autoFocus
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Any detail that helps (optional)"
        rows={2}
      />
      <div className="flex items-center gap-2">
        <span className="text-xs text-ink-faint">Severity</span>
        {SEVERITIES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => setSeverity(s.value)}
            className={cn(
              'text-xs px-2.5 py-1 rounded-full border transition-colors',
              severity === s.value
                ? 'border-ink bg-ink text-bg'
                : 'border-line text-ink-dim hover:bg-fill-soft',
            )}
          >
            {s.label}
          </button>
        ))}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
      />
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()}>
          <ImagePlus className="w-3.5 h-3.5" />
          {files.length > 0 ? `${files.length} photo(s)` : 'Add photo'}
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onDone} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={busy}>
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Report
          </Button>
        </div>
      </div>
    </div>
  );
}
